import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import BillingoService from '../../../Services/BillingoService';

/**
 * The line shown instead of something still loading ("Loading documents…")
 * or that failed to ("Couldn't load documents", with Retry); nothing once it
 * is in.
 */
function LoadNote({
  what, loading, failed, onRetry,
}) {
  if (loading) {
    return (
      <span className="text-muted">
        <Spinner size="sm" animation="border" className="me-1" />
        {`Loading ${what}…`}
      </span>
    );
  }
  if (failed) {
    return (
      <span className="text-danger">
        {`Couldn't load ${what}`}
        <Button variant="link" size="sm" className="p-0 ms-2 align-baseline" onClick={onRetry}>
          Retry
        </Button>
      </span>
    );
  }
  return null;
}

/**
 * Every document of the given rows' bookings (`{ booking_ids }` each), in ONE
 * request — refetched whenever `rows` is a new array, i.e. whenever the list
 * reloads, and on `reload()`. The Invoicing lists, and Order Details'
 * documents for one booking.
 *
 * Returns `documentsOf(row)`, `noteFor(documents)` — the loading, failure
 * (with retry) or "no documents" line to show instead of a row's documents,
 * or null when there are documents to show — and `reload()`.
 */
export default function useBookingDocuments(rows) {
  const [state, setState] = useState({ documents: [], loading: false, error: false });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const ids = [...new Set(rows.flatMap((row) => row.booking_ids))];
    if (!ids.length) return undefined;
    let live = true;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    (async () => {
      try {
        const documents = await BillingoService.listDocumentsForBookingIds(ids);
        if (live) setState({ documents, loading: false, error: false });
      } catch {
        if (live) setState({ documents: [], loading: false, error: true });
      }
    })();
    return () => { live = false; };
  }, [rows, attempt]);

  const documentsOf = (row) => state.documents.filter(
    (doc) => (doc.booking_ids || []).some((id) => row.booking_ids.includes(id)),
  );

  const reload = () => setAttempt((n) => n + 1);

  const noteFor = (documents) => {
    if (state.loading || state.error) {
      return (
        <LoadNote
          what="documents"
          loading={state.loading}
          failed={state.error}
          onRetry={reload}
        />
      );
    }
    return documents.length ? null : <span className="text-muted">No documents yet</span>;
  };

  return { documentsOf, noteFor, reload };
}
